import { useAppCustomers } from '@/hooks';
import { useTranslate } from '@/translations';
import { AppFeatures, Inspection } from '@/types';
import { currencyFormatted } from '@/utils/helpers';
import React, { FC } from 'react';

interface InspectionPointsTableProps {
  data: Inspection;
  estimatedFinalGradePoints?: number;
  estimatedFinalGradeScore?: number;
}

const EstimatedValue = ({
  current,
  latest,
}: {
  current: number;
  latest?: number;
}) => {
  if (latest === undefined || latest === current) return <>{current}</>;
  return (
    <>
      {current}
      <span
        data-testid="estimated-final-grade"
        style={{
          color: latest < current ? '#12b76a' : '#d92d20',
          fontWeight: 600,
        }}
      >
        {` -> ${latest}`}
      </span>
    </>
  );
};

export const InspectionPointsTable: FC<InspectionPointsTableProps> = ({
  data,
  estimatedFinalGradePoints,
  estimatedFinalGradeScore,
}) => {
  const { t } = useTranslate();
  const { hasDamageApproveAccess, hasFeatureAccess } = useAppCustomers();

  return (
    <table style={{ width: '100%', lineHeight: 1.1 }}>
      <thead>
        <tr>
          <th aria-label="empty">&nbsp;</th>
          <th className="fw-semibold">{t('cumulative')}</th>
          <th className="fw-semibold">{t('final')}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="fw-semibold">{t('cost')}</td>
          <td className="text-muted">
            {currencyFormatted(
              hasDamageApproveAccess
                ? data.cumulativeApprovalCost
                : data.cumulativeCost,
            )}
          </td>
          <td className="text-muted">
            {currencyFormatted(
              hasDamageApproveAccess ? data.finalApprovalCost : data.finalCost,
            )}
          </td>
        </tr>
        {hasFeatureAccess(AppFeatures.InspectionGradePoints) ? (
          <tr>
            <td className="fw-semibold">{t('gradePoints')}</td>
            <td className="text-muted">{data.cumulativeGradePoints}</td>
            <td className="text-muted">
              <EstimatedValue
                current={data.finalGradePoints}
                latest={estimatedFinalGradePoints}
              />
            </td>
          </tr>
        ) : null}
        {hasFeatureAccess(AppFeatures.InspectionGradeScore) ? (
          <tr>
            <td className="fw-semibold">{t('gradeScore')}</td>
            <td className="text-muted">{data.cumulativeGradeScore}</td>
            <td className="text-muted">
              <EstimatedValue
                current={data.finalGradeScore}
                latest={estimatedFinalGradeScore}
              />
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
};
